import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Page, Group, Layout, Button, ExcelUpload } from "@/comn/components";
import {
    useForm,
    useToast,
    useFetch,
    useModal,
    useAuth,
    useStore,
    useGrid,
    usePopup,
    useResource,
    useExcel,
    usePage,
} from "@/comn/hooks";
import {
    APIS,
    SF_APP_DTL_VRFN_INFO,
    SF_APP_RGST_VRFN_APRV_RSLT,
} from "@/tz/go/egaz/osci/lpco/app/service/OsciLpcoAprvService";

/**
 * LPCO Application Approval Result
 * !== LPCO 신청서 승인 결과 ==!
 */
export const OSCI0201008S = (props: any) => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0017", "auth_osci0018"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변화 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!

    const { close, postMessage, getParams } = usePopup();
    const params = getParams(); // 부모창에서 전달된 Parameter

    let errmsg: any;

    const [sbmtVisible, setSbmtVisible] = useState(false);
    const [exmpnTaxVisible, setExmpnTaxVisible] = useState(false);

    const [excludeAuditCode, setExcludeAuditCode] = useState<any[]>(["VF", "IP", "AP", "RS"]);

    /*
     * Code Resource
     * !== 사용할 Code 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0131" },
            { area: "comnCd", comnCd: "OSC0142" },
        ],
    });

    const form = {
        appDtlVrfnAprvInfo: useForm({
            defaultSchema: SF_APP_DTL_VRFN_INFO,
        }),
        appRgstVrfnAprvRslt: useForm({
            defaultSchema: SF_APP_RGST_VRFN_APRV_RSLT,
        }),
    };

    /*
     * Application Item Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get Application Information !== 신청서 정보 조회 ==!.
        getAppAprvInfo: useFetch({
            api: (data) => APIS.getAppAprvInfo({ reffId: data.appId }),
            onSuccess: (data) => {
                if (data.appAprvInfo!) {
                    form.appRgstVrfnAprvRslt.setValues(data.appAprvInfo.content);
                    if (data.appAprvInfo.content.prcssStatCd!) {
                        if (
                            "ES" == params.appInfo.appStatCd &&
                            ("FV" == data.appAprvInfo.content.prcssStatCd ||
                                data.appAprvInfo.content.exmpnTaxTpCd.length > 0)
                        ) {
                            setExmpnTaxVisible(true);
                        } else {
                            setExmpnTaxVisible(false);
                        }
                        setSbmtVisible(true);
                    } else {
                        if ("ES" == params.appInfo.appStatCd) {
                            setExmpnTaxVisible(true);
                        }
                    }
                }
                const nregYn = data?.appAprvInfo?.content?.nregYn || "";
                const prcsRoleTpCd = data?.prcsInfo?.content?.prcsRoleTpCd || "";
                const prevPrcsRoleTpCd = data?.prcsInfo?.content?.prevPrcsRoleTpCd || "";

                if (nregYn === "Y") {
                    setExcludeAuditCode([...APIS.getExcludeAuditRsltCdList("NR", prevPrcsRoleTpCd)]);
                } else {
                    setExcludeAuditCode([
                        ...APIS.getExcludeAuditRsltCdList(
                            prcsRoleTpCd,
                            prevPrcsRoleTpCd,
                            params.appInfo.appCdNo,
                            data?.appAprvInfo?.content?.zbsClearanceNregMarkYn,
                        ),
                    ]);
                }
            },
            onError: (error) => {
                errmsg = error.response.data.errorCode == -1 ? "msg.osc.00015" : error.response.data.errorMessage;

                modal.openModal({
                    content: errmsg,
                });
            },
        }),
        saveAppAprvRslt: useFetch({
            api: (data) => {
                return APIS.saveAppAprvRslt({ ...data });
            },
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.osc.00006",
                    onConfirm: () => {
                        handler.setInit();
                        modal.postMessage({ rslt: "save" });
                    },
                    onClose: () => {
                        handler.setInit();
                    },
                });
            },
            onError: (error) => {
                const errmsg = error.response.data.errorCode == -1 ? "msg.osc.00015" : error.response.data.errorMessage;

                modal.openModal({
                    content: errmsg,
                });
            },
        }),
        sbmtAppAprvRslt: useFetch({
            api: (data) => {
                return APIS.sbmtAppAprvRslt({ ...data });
            },
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.osc.00006",
                    onConfirm: () => {
                        modal.postMessage({ rslt: "sbmt" });
                    },
                    onClose: () => {
                        modal.postMessage({ rslt: "sbmt" });
                    },
                });
            },
            onError: (error) => {
                const errmsg = error.response.data.errorCode == -1 ? "msg.osc.00015" : error.response.data.errorMessage;

                modal.openModal({
                    content: errmsg,
                });
            },
        }),
    };

    /*
     * Application Item Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        setInit: async () => {
            await form.appDtlVrfnAprvInfo.setValues(params.appInfo);
            await handler.getAppAprvInfo();
        },
        getAppAprvInfo: async () => {
            fetch.getAppAprvInfo.fetch(params.appInfo);
        },
        changePrcssStatCd: (value: any) => {
            if ("FV" == value && "ES" == params.appInfo.appStatCd) {
                setExmpnTaxVisible(true);
            } else {
                setExmpnTaxVisible(false);
            }
        },
        // Registration validation of application verification approval results !== 신청서 심사승인결과 등록 유효성 검사 ==!
        validateRgstVrfnAprvRslt: async (type: any) => {
            const chkRslt = await form.appRgstVrfnAprvRslt.validate();

            if (chkRslt) {
                form.appRgstVrfnAprvRslt.setValue("reffId", params.appInfo.appId);
                const data = { ...form.appRgstVrfnAprvRslt.getValues() };
                data.amdTpCd = params.appInfo.amdTpCd;

                if (type == "save") {
                    modal.openModal({
                        content: "msg.osc.00090",
                        onConfirm: () => {
                            fetch.saveAppAprvRslt.fetch(data);
                        },
                    });
                } else if (type == "sbmt") {
                    modal.openModal({
                        content: "msg.osc.00092",
                        onConfirm: () => {
                            fetch.sbmtAppAprvRslt.fetch(data);
                        },
                    });
                }
            }
        },
    };

    useEffect(() => {
        handler.setInit();
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Layout direction="col">
                        <Layout>
                            <Layout.Left>
                                <Group.Title title={"T_VRFN_APRV_RSLT"} titleSize={1}></Group.Title>
                            </Layout.Left>
                        </Layout>

                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.appDtlVrfnAprvInfo.schema.appNo} />
                                <Group.Control {...form.appDtlVrfnAprvInfo.schema.ucrNo} />
                            </Group.Row>

                            <Group.Row>
                                <Group.Control {...form.appDtlVrfnAprvInfo.schema.appNm} />
                                <Group.Control {...form.appDtlVrfnAprvInfo.schema.sbmtDtm} />
                            </Group.Row>

                            <Group.Row>
                                <Group.Control
                                    {...form.appRgstVrfnAprvRslt.schema.prcssStatCd}
                                    controlSize={10}
                                    excludes={[...excludeAuditCode]}
                                    onChange={(value) => {
                                        handler.changePrcssStatCd(value);
                                    }}
                                />
                            </Group.Row>

                            <Group.Row>
                                <Group.Control {...form.appRgstVrfnAprvRslt.schema.prcssRsnCn} controlSize={10} />
                            </Group.Row>

                            {exmpnTaxVisible && (
                                <Group.Row>
                                    <Group.Control {...form.appRgstVrfnAprvRslt.schema.exmpnTaxTpCd} controlSize={10} />
                                </Group.Row>
                            )}

                            <Group.Row>
                                <Group.Control {...form.appRgstVrfnAprvRslt.schema.rmrk} controlSize={10} />
                            </Group.Row>
                        </Group.Section>
                    </Layout>
                    <Layout direction="row">
                        <Layout.Right>
                            <Button
                                role="save"
                                onClick={() => {
                                    handler.validateRgstVrfnAprvRslt("save");
                                }}
                            />
                            {sbmtVisible && (
                                <Button
                                    role="submit"
                                    onClick={() => {
                                        handler.validateRgstVrfnAprvRslt("sbmt");
                                    }}
                                >
                                    {t("L_CFRM")}
                                </Button>
                            )}
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
        </Page>
    );
};
